import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { List as MovableList, arrayMove } from 'react-movable'
import ListItemIcon from '@mui/material/ListItemIcon'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { ButtonBase, List as MuiList, ListItem } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'
import { Text2, Caption } from 'ui/shorthands/Texts'
import { DepartmentListItemProps } from 'types/common'

const useStyles = makeStyles()((theme: Theme) => ({
  container: {
    padding: theme.spacing(0.5, 0),
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  itemIcon: {
    minWidth: 32,
    color: theme.palette.text.secondary,
    cursor: 'row-resize',
  },
  selected: {
    backgroundColor: theme.palette.primary.light,
    '& svg,& span,& small': {
      // NOTE: 必要な子孫要素のみ色指定
      color: theme.palette.primary.main,
    },
  },
  buttonBase: {
    display: 'flex',
    justifyContent: 'space-between',
    flex: 'auto 1',
  },
  text: {
    textAlign: 'left',
  },
  status: {
    whiteSpace: 'nowrap',
    paddingLeft: theme.spacing(0.5),
  },
}))

export type SortableMenuProps = {
  list: Array<DepartmentListItemProps>
  onChange: (id: number, order: number) => void
  currentId?: number
  onSelect: (departmentId: number) => void
}

const SortableMenu: React.FC<SortableMenuProps> = ({
  list,
  onChange,
  currentId,
  onSelect,
}: SortableMenuProps) => {
  const { t } = useTranslation()
  const { classes, cx } = useStyles()

  const [items, setItems] = React.useState<DepartmentListItemProps[]>([])

  useEffect(() => {
    setItems(
      list.sort((a, b) => {
        return a.display_order - b.display_order
      })
    )
  }, [list])

  return (
    <MovableList
      values={items}
      onChange={({ oldIndex, newIndex }) => {
        setItems(arrayMove(items, oldIndex, newIndex))
        onChange(items[oldIndex].id, newIndex + 1) // NOTE: 渡すのは順番なので+1
      }}
      renderList={({ children, props }) => (
        <MuiList {...props} disablePadding>
          {children}
        </MuiList>
      )}
      renderItem={({ value, props }) => {
        const selected: boolean = value.id === currentId
        return (
          <ListItem
            {...props}
            divider
            className={cx(classes.itemIcon, selected && classes.selected)}
          >
            <div className={classes.container}>
              <ListItemIcon className={classes.itemIcon}>
                <DragIndicatorIcon />
              </ListItemIcon>
              <ButtonBase
                className={classes.buttonBase}
                onClick={(e) => {
                  onSelect && onSelect(value.id)
                  e.stopPropagation()
                  e.preventDefault()
                }}
              >
                <div className={classes.text}>
                  <Text2 className={classes.text}>{value.name}</Text2>
                </div>
                <div className={classes.status}>
                  <Caption>{value.is_open ? t('公開') : t('非公開')}</Caption>
                </div>
              </ButtonBase>
            </div>
          </ListItem>
        )
      }}
    />
  )
}

SortableMenu.defaultProps = {
  list: [],
  currentId: undefined,
}

export default SortableMenu
